import { getDoc, doc } from 'firebase/firestore'
import { convertToLocalTime } from '@helpers/convertToLocalTime'
import { getTeamsData } from './getTeamsData'
import { logger } from '@helpers/logger' // Updated to use the new log structure

// 30 minutes in milliseconds
const CACHE_DURATION = 30 * 60 * 1000

export const getEventsAndTeamsData = async (db, leaguesData, setLeaguesData, teamsData, setTeamsData, league, strCurrentSeason, sport, noFilter, forceRefresh = false) => {
  // Use a cache key based on the noFilter flag
  const filterKey = noFilter ? 'noFilter' : 'default'
  const params = { league, strCurrentSeason, sport, noFilter }
  const logComplete = logger.serviceRequest('getEventsAndTeamsData', params)

  try {
    // Check if events and teams exist in leaguesData for this sport/league/filterKey
    // Skip cache if forceRefresh is true
    if (!forceRefresh && leaguesData.sports && leaguesData.sports[sport] && leaguesData.sports[sport][league] && leaguesData.sports[sport][league][filterKey]) {
      const cacheEntry = leaguesData.sports[sport][league][filterKey]
      // If the cache entry is fresh (within 30 minutes), return it
      if (Date.now() - cacheEntry.lastUpdated < CACHE_DURATION) {
        logComplete() // Call the completion logging function
        return {
          events: cacheEntry.events,
          teams: cacheEntry.teams,
        }
      }
    }

    // Fetch the events document from Firestore
    const eventsDocumentRef = doc(db, 'sports', sport, 'leagues', league, 'seasons', strCurrentSeason, 'eventsAll', 'events')
    const eventsDocumentSnapshot = await getDoc(eventsDocumentRef)

    if (!eventsDocumentSnapshot.exists()) {
      logger.error('getEventsAndTeamsData', `No eventsDocument found for ${league} in ${strCurrentSeason}`)
      logComplete()
      return { events: [], teams: {} }
    }

    const eventsDocument = eventsDocumentSnapshot.data().eventResults
    const now = window.GlobalDate.newDate() // Current date and time

    // Extract events, convert to local time, filter, and sort (using UTC parsing)
    const events = Object.values(eventsDocument)
      .map((event) => {
        const { eventDateLocal, eventTimeLocal } = convertToLocalTime(event.dateEvent, event.strTime)
        return { ...event, eventDateLocal, eventTimeLocal }
      })
      // Filter for events that haven't started yet (using dateEvent + 'T' + strTime + 'Z')
      .filter((event) => {
        if (noFilter) return true
        // Append 'Z' to interpret as UTC
        const eventDateTime = new Date(event.dateEvent + 'T' + event.strTime + 'Z')
        return eventDateTime >= now
      })
      // Sort events in ascending order (soonest first) based on UTC date/time
      .sort((a, b) => {
        const eventA = new Date(a.dateEvent + 'T' + a.strTime + 'Z')
        const eventB = new Date(b.dateEvent + 'T' + b.strTime + 'Z')
        return eventA - eventB
      })

    // Fetch teams data
    const teams = await getTeamsData(db, sport, league, strCurrentSeason, teamsData, setTeamsData)

    // Update the cache in leaguesData with a timestamp
    setLeaguesData((prevLeaguesData) => {
      const newLeaguesData = { ...prevLeaguesData }
      if (!newLeaguesData.sports) {
        newLeaguesData.sports = {}
      }
      if (!newLeaguesData.sports[sport]) {
        newLeaguesData.sports[sport] = {}
      }
      if (!newLeaguesData.sports[sport][league]) {
        newLeaguesData.sports[sport][league] = {}
      }

      newLeaguesData.sports[sport][league][filterKey] = {
        events,
        teams,
        lastUpdated: Date.now(),
      }
      return newLeaguesData
    })
    logComplete() // Call the completion logging function
    return { events, teams }
  } catch (error) {
    logger.error('getEventsAndTeamsData', error)
    logComplete() // Call the completion logging function even on error
    return { events: [], teams: {} }
  }
}
